import {
  FormField,
  FormFieldType,
  FormChoiceResponse,
  FormChoiceResponseType,
} from 'types';

// Normalize legacy saved answers at the form boundary. Typed responses, including
// Other text, stay untouched; malformed data remains visible to validation.
export const normalizeChoiceResponseValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) =>
      typeof item === 'string'
        ? { value: item, type: FormChoiceResponseType.OPTION }
        : item
    );
  }
  if (typeof value === 'string') {
    return value === '' ? null : { value, type: FormChoiceResponseType.OPTION };
  }
  return value;
};

export const isFormChoiceResponse = (
  value: unknown
): value is FormChoiceResponse =>
  Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(FormChoiceResponseType).includes(
      (value as FormChoiceResponse).type
    ) &&
    typeof (value as FormChoiceResponse).value === 'string'
  );

// Free-text Other responses are ungraded, consistently across field and page UI.
export const isGradedSelect = (field: FormField) =>
  field.type === FormFieldType.SELECT &&
  !!field.options?.correctOptions &&
  !field.options.allowOther;

export const areSavedChoicesCorrect = (
  value: unknown,
  correctOptions: string[]
) => {
  const normalized = normalizeChoiceResponseValue(value);
  const responses = Array.isArray(normalized) ? normalized : [normalized];
  if (responses.length === 0) return false;
  if (
    !responses.every(
      (response) =>
        isFormChoiceResponse(response) &&
        response.type === FormChoiceResponseType.OPTION
    )
  )
    return false;
  const savedValues = new Set(
    responses.map((response) => (response as FormChoiceResponse).value)
  );
  const expectedValues = new Set(correctOptions);
  return (
    savedValues.size === expectedValues.size &&
    Array.from(expectedValues).every((option) => savedValues.has(option))
  );
};
